import LoaderMask from '@loggi/ui/components/loader-mask';
import { locationModel } from 'app/app.models';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import authService from 'shared/auth/auth.service';
import { PATH_PACKAGE_TRACKING } from 'tracker.routes';
import queryString from 'query-string';
import DefaultError from '../default-error/default-error.component';
import MainView from '../main-view/main-view.component';
import privateRouteService from './private-route.service';
import getPublicTrackerPathname from './private-route.utils';

export const RECEIVER_IS_NOT_AUTHENTICATED = 'receiver_is_not_authenticated';
export const PACKAGE_ID_IS_NOT_VALID = 'package_id_is_not_valid';
export const DNR_QUERY_STRING = 'pdcbr_uuid';

export default class PrivateRoute extends Component {
  static propTypes = {
    match: PropTypes.shape({
      params: PropTypes.shape({
        packageId: PropTypes.string.isRequired
      })
    }).isRequired,
    component: PropTypes.element.isRequired,
    location: PropTypes.shape(locationModel).isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      queryProps: null,
      error: null,
      pathname: null
    };
  }

  componentDidMount() {
    const {
      match: {
        params: { packageId }
      }
    } = this.props;
    if (!packageId || !parseFloat(packageId)) {
      // it should redirect to root tracker route path
      // to not check the authentication for a nonexistent package id
      this.setState({
        error: PACKAGE_ID_IS_NOT_VALID,
        pathname: PATH_PACKAGE_TRACKING
      });
    }
    return this._checkAuthentication(packageId);
  }

  _redirect = (packageId, authenticationError) => {
    const error = authenticationError;
    return privateRouteService
      .getMorePackageInfo(packageId)
      .then(queryProps => {
        const {
          packageTracker: {
            packageInfo: { trackingKey, companyHash }
          }
        } = queryProps;
        this.setState({
          error,
          pathname: getPublicTrackerPathname(trackingKey, companyHash)
        });
      })
      .catch(err => {
        this.setState({ error, pathname: PATH_PACKAGE_TRACKING });
        throw new Error(
          `Could not redirect to public tracker - package id: ${packageId} - error: ${err}`
        );
      });
  };

  _checkAuthentication(packageId) {
    authService
      .checkAuthentication(packageId)
      .then(queryProps => {
        // Client will meet the condition {"privatePackageTracker": null} as an unauthenticated user by contract
        // and therefore it will then invoke its proper component in order to replies with a suitable
        // error message.
        if (!queryProps.privatePackageTracker) {
          const error = RECEIVER_IS_NOT_AUTHENTICATED;
          return this._redirect(packageId, error);
        }

        return this.setState({ queryProps });
      })
      .catch(error => {
        if (error === RECEIVER_IS_NOT_AUTHENTICATED) {
          // it should redirect to public tracker where the receiver will authenticate
          return this._redirect(packageId, error);
        }
        this.setState({ error, pathname: PATH_PACKAGE_TRACKING });
        throw new Error(
          `[private package tracker authentication error] - package id: ${packageId} - error: ${error}`
        );
      });
  }

  render() {
    const {
      component: ComponentToRender,
      match: {
        params: { packageId }
      },
      location
    } = this.props;
    const { queryProps, error, pathname } = this.state;

    if (!queryProps && !error) {
      return (
        <MainView className="tracker-content">
          <LoaderMask />
        </MainView>
      );
    }

    if (error) {
      if (pathname) {
        const query = queryString.parse(location.search);
        if (query[DNR_QUERY_STRING]) {
          return (
            <Redirect
              to={`${pathname}?${DNR_QUERY_STRING}=${query[DNR_QUERY_STRING]}`}
            />
          );
        }
        return (
          <Redirect
            to={{
              pathname,
              state: { from: location, packageId }
            }}
          />
        );
      }
      return <DefaultError />;
    }

    return <ComponentToRender {...this.props} {...queryProps} />;
  }
}
